import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listProductosRpc, getImageSignedUrl } from '../supabase/Productos';
import { useAuth, useNumDocumento } from '../../context/AuthContext';
import './Home.css';
import logo from '../Login/logo.png';
import Usuario from "../supabase/Usuario";
import { updateProductoEstado } from '../supabase/Productos';
import trashIcon from './trash-svgrepo-com.svg'


const PROVINCIAS_CR = [
  'San José','Alajuela','Cartago','Heredia','Guanacaste','Puntarenas','Limón'
];

export default function Home() {
  const navigate = useNavigate();
  const numDocumento = useNumDocumento();
  const { user, logout } = useAuth();
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [provincia, setProvincia] = useState('');
  const [idTipoEntrega, setIdTipoEntrega] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageUrls, setImageUrls] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  // Cargar productos con filtros
  useEffect(() => {
    setLoading(true);
    setError('');
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      const { data, error } = await listProductosRpc({
        search,
        categoria,
        provincia,
        idTipoEntrega,
      });
      if (!ctrl.signal.aborted) {
        if (error) setError('No se pudieron cargar los productos');
        setItems(data || []);
        setLoading(false);
      }
    }, 250);
      if (user) {
          async function checkAdmin() {
              const admin = await Usuario.isAdmin(user.numDocumento);
              setIsAdmin(admin);
          }
          checkAdmin();
      }
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [search, categoria, provincia, idTipoEntrega, user]);

  // Resolver URLs firmadas para imágenes cuando cambian los items
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all((items || []).map(async (it) => {
        const path = it.rutaImagen || it.imgPath || it.imgpath || it.image_path || it.imagePath;
        if (!path) return [it.id, null];
        const { url, error } = await getImageSignedUrl(path, 60 * 60);
        if (error) console.warn('[Home] signedUrl error for', { id: it.id, path }, error);
        return [it.id, url || null];
      }));
      if (!cancelled) setImageUrls(Object.fromEntries(pairs));
    })();
    return () => { cancelled = true; };
  }, [items]);

  const onClearFilters = () => {
    setSearch(''); setCategoria(''); setProvincia(''); setIdTipoEntrega('');
  };

  const headerActions = useMemo(() => (
    <div className="header-actions">
      <button className="btn" onClick={() => navigate('/publish')}>Publicar un Producto</button>
    </div>
  ), [navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const showEmpty = !loading && !error && items.length === 0;

  return (
      <div className="app-container">
          <div className="home-page">
          <header className="topbar" role="banner">
            <div className="brand">
              <img src={logo} alt="Logo reANIDArt - Muestra 1 pájaro con dos polluelos en un nido de ramas con hojas alrededor" className="brand-logo" />
              <nav className="nav" aria-label="navegación principal">
                <button className="nav-link active" onClick={() => navigate('/home')}>Inicio</button>
                <button className="nav-link" onClick={() => navigate('/profile')}>Mi nido</button>
              </nav>
              <button className="logout-btn" onClick={handleLogout} title="Cerrar sesión" aria-label="Cerrar sesión">↪</button>
            </div>
            <div className="right" />
              <title>ReANIDArt - Página principal</title>
          </header>

          <main className="content">
            <div className="search-row">
              <div className="search-box">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar" aria-label="Buscar productos" />
                <button className="icon">🔍</button>
              </div>
              <div className="filters">
                <select value={categoria} onChange={(e)=>setCategoria(e.target.value)} aria-label="Categoría">
                  <option value="">Categoría</option>
                  <option value="Producto">Producto</option>
                  <option value="Servicio">Servicio</option>
                </select>
                <select value={provincia} onChange={(e)=>setProvincia(e.target.value)} aria-label="Ubicación">
                  <option value="">Ubicación</option>
                  {PROVINCIAS_CR.map(p=> <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={idTipoEntrega} onChange={(e)=>setIdTipoEntrega(e.target.value)} aria-label="Tipo de entrega">
                  <option value="">Tipo de entrega</option>
                  <option value="1">Entrega en persona</option>
                  <option value="2">Envío</option>
                </select>
                {headerActions}
              </div>
            </div>

            {error && <div className="error">{error}</div>}

            {loading ? (
              <div className="list" aria-busy>
                {Array.from({ length: 4 }).map((_, i) => (
                  <article key={`sk-${i}`} className="card skeleton" aria-hidden>
                    <div className="thumb"><div className="sk sk-thumb" /></div>
                    <div className="card-body">
                      <div className="sk sk-title" />
                      <div className="sk sk-line" />
                      <div className="sk sk-price" />
                      <div className="sk sk-line short" />
                    </div>
                  </article>
                ))}
              </div>
            ) : showEmpty ? (
              <div className="empty-state">
                <h3>Sin resultados</h3>
                <p>No se encontraron productos con estos filtros.</p>
                <div className="empty-actions">
                  <button className="btn" onClick={onClearFilters}>Limpiar filtros</button>
                </div>
              </div>
            ) : (
        <div className="list" role="list">
                {items.map(item => (
                  <article
                    key={item.id}
              className="card"
                    onClick={() => navigate(`/product/${item.id}`)}
                    role="button"
                    tabIndex={0}
              aria-label={`Ver producto: ${item?.titulo || 'Producto'}`}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/product/${item.id}`); }}
                  >
                    <div className="thumb" aria-hidden>
                      {imageUrls[item.id]
                        ? <img className="thumb-img" src={imageUrls[item.id]} alt={item?.titulo || 'Producto'} />
                        : <div className="placeholder" />}
                    </div>
                    <div className="card-body">
                      <div className="title">{item?.titulo || 'Producto'}</div>
                      <div className="secondary">{(item?.categoria || 'Producto')}</div>
                      <div className="price">₡ {Number(item.precio||0).toLocaleString('es-CR')}</div>
                      <div className="secondary">{item?.direccion || 'Ubicación'}</div>
                    </div>

                    {/* Delete button for admins */}
                    {isAdmin && (
                <button
                        className="delete-btn-icon"
                        onClick={async (e) => {
                          e.stopPropagation(); // prevent navigating
                          if (!window.confirm('¿Estás seguro de eliminar esta publicación?')) return;

                          try {
                            await updateProductoEstado(item.id, 4);
                            setItems(prev => prev.filter(it => it.id !== item.id));
                          } catch (err) {
                            console.error(err);
                            alert('Error al eliminar el producto');
                          }
                        }}
                  title="Eliminar publicación"
                  aria-label={`Eliminar publicación ${item?.titulo || ''}`}
                      >
                        <img src={trashIcon} alt="logo cantidad de varitas"/>
                      </button>
                    )}
                  </article>
                ))}
              </div>
            )}

            {!showEmpty && (
              <div className="filters-footer">
                <button className="link" onClick={onClearFilters}>Limpiar filtros</button>
              </div>
            )}
          </main>
        </div>
      </div>
  );
}